import { Bid } from '@prisma/client';
import _ from 'lodash';

export interface NoncedBid extends Bid {
    nonceId?: string;
    currentHighest: boolean;
}

export const sortBids = (bids: NoncedBid[]): NoncedBid[] => {
    if (!bids || bids.length === 0) return [];

    const nonceOf = (bid: NoncedBid) => bid.nonceId || bid.userId;

    // Separate active and removed bids. Removed go to the end unchanged in order by amount desc, createdAt desc.
    const active: NoncedBid[] = bids.filter(b => !b.removed);
    const removed: NoncedBid[] = bids.filter(b => b.removed)
        .sort((a, b) => (b.amount - a.amount) || (new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));

    if (active.length === 0) return removed;

    // Group active bids by amount (descending amounts)
    const amountToBids = new Map<number, NoncedBid[]>();
    for (const bid of active) {
        const arr = amountToBids.get(bid.amount) || [];
        arr.push(bid);
        amountToBids.set(bid.amount, arr);
    }

    const sortedAmountsDesc = Array.from(amountToBids.keys()).sort((a, b) => b - a);
    const highestAmount = sortedAmountsDesc[0];
    const sortedAmountsAsc = _.reverse([...sortedAmountsDesc]);

    // Run a pass over a provided amount order using the same adjacency logic
    const runPass = (amountOrder: number[]): NoncedBid[] => {
        // Within each amount level, baseline order: winner first at highest level, then by createdAt desc (earliest last)
        const normalizeLevel = (amount: number, lastNonce: string | undefined): NoncedBid[] => {
            const list = amountToBids.get(amount) || [];
            const baseline = [...list].sort((a, b) => {
                // Winner first only for the top amount.
                if (amount === highestAmount) {
                    if (a.currentHighest && !b.currentHighest) return -1;
                    if (!a.currentHighest && b.currentHighest) return 1;
                }
                const aTime = new Date(a.createdAt).getTime();
                const bTime = new Date(b.createdAt).getTime();
                return bTime - aTime;
            });

            // Greedily include bids while avoiding consecutive same nonce within the level and vs lastNonce.
            const chosen: NoncedBid[] = [];
            for (const bid of baseline) {
                const prevNonce = chosen.length > 0 ? nonceOf(chosen[chosen.length - 1]) : lastNonce;
                if (prevNonce && nonceOf(bid) === prevNonce) continue;
                chosen.push(bid);
            }
            return chosen;
        };

        const getLevelNonces = (amount: number): Set<string> => {
            const set = new Set<string>();
            for (const bid of amountToBids.get(amount) || []) {
                set.add(nonceOf(bid));
            }
            return set;
        };

        const resultActive: NoncedBid[] = [];
        let lastNonce: string | undefined = undefined;

        for (let i = 0; i < amountOrder.length; i++) {
            const amount = amountOrder[i];
            let level = normalizeLevel(amount, lastNonce);

            // Lookahead to next amount: if the next level only has bids from a single nonce that equals
            // the last nonce of this level, prefer to drop trailing bids in this level to end on a different
            // nonce so the next level can display at least one bid without violating adjacency.
            const nextAmount = amountOrder[i + 1];
            if (level.length > 0 && typeof nextAmount === 'number') {
                const nextNonces = getLevelNonces(nextAmount);
                if (nextNonces.size === 1) {
                    const onlyNextNonce = Array.from(nextNonces)[0];
                    // If we'd end this level on the same nonce, trim trailing bids until it's different or only one left.
                    while (level.length > 0 && nonceOf(level[level.length - 1]) === onlyNextNonce && level.length > 1) {
                        level.pop();
                    }
                    // If still ends with same nonce and lastNonce exists and equals onlyNextNonce, we can't include this level's last
                    // without blocking next level. In that rare case, keep only the first element to ensure winner-on-top rule at top level
                    // and to maximize alternation overall.
                    if (level.length > 0 && nonceOf(level[level.length - 1]) === onlyNextNonce && lastNonce === onlyNextNonce && level.length > 1) {
                        level = [level[0]];
                    }
                }
            }

            for (const bid of level) {
                // Final safety to avoid any consecutive same nonce across levels
                if (lastNonce && nonceOf(bid) === lastNonce) continue;
                resultActive.push(bid);
                lastNonce = nonceOf(bid);
            }
        }

        return resultActive;
    };

    const activeDesc = runPass(sortedAmountsDesc);
    const activeAsc = runPass(sortedAmountsAsc);

    // Prefer the ordering that contains the current highest bid when only one does.
    const hasHighestDesc = activeDesc.some(b => b.currentHighest);
    const hasHighestAsc = activeAsc.some(b => b.currentHighest);

    const chosenActive = (hasHighestDesc !== hasHighestAsc)
        ? (hasHighestDesc ? activeDesc : _.reverse(activeAsc))
        : (activeAsc.length > activeDesc.length ? _.reverse(activeAsc) : activeDesc);

    return [...chosenActive];
}; 